import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { Space, Dropdown } from '@fuxi/eevee-ui';
import { useTranslation } from 'react-i18next';
import { useAntLocale } from '@fuxi/eevee-hooks';
import classnames from 'classnames/bind';
import { encodeNextUrl, ysfOpen } from '@commonPackage/utils';
import PubSub from 'pubsub-js';
import { EeveeIcon, PCHeaderDropAvatar } from 'common-package/src/components';
import { OemPageMode } from 'common-package/src/constants';
import {
  useCurrentPageInfo,
  useUserInfo,
  useAppSelector,
  useCurrentActivityInfo,
  getCorporateUrl,
  useInvokeAreaPrefix,
} from '@commonPackage/hooks';
import { HAS_CONFIG_LANG_PAGE, YAOTAI_LOGO_BLACK, YAOTAI_LOGO_BLACK_EN } from '@commonPackage/constants';
import { useYaotaiGlobalContextState } from '@commonPackage/contexts';
import { EN, SubPackageSlugEnum, ZH, checkIsYaotaiUser, useLoginInfo } from '@yaotai/common-utils';
import styles from './index.module.less';

export const TABS_STICKY_STATUS = 'sticky_status';
const cx = classnames.bind(styles);

export const NavHeader = ({
  style,
  oemPageMode = 'activity',
}: PropsWithChildren<{ oemPageMode?: OemPageMode; style: React.CSSProperties }>) => {
  const { isLogin } = useLoginInfo();
  const location = useLocation();
  const { language, changeLanguage } = useAntLocale();
  const { t } = useTranslation();
  const { defaultCardInfo } = useAppSelector(state => state.card);
  const userInfo = useUserInfo({ monorepoType: 'center' });
  const { login_type } = userInfo;
  const { currentPageMatch } = useCurrentPageInfo();
  const { name: activityName } = useCurrentActivityInfo();
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const { login } = useInvokeAreaPrefix({ needSlug: false });

  const { oem, spaceAuth, currentAid, isMainLogoOemCustom } = useYaotaiGlobalContextState();
  const { hasSpacePermission } = spaceAuth;

  const isActivityHomePage = currentPageMatch === '/activity/:aid';
  const isHasConfigLang = HAS_CONFIG_LANG_PAGE.some(item => location.pathname.includes(item));

  const changeLang = () => {
    const lang = language === ZH ? EN : ZH;
    localStorage.setItem('HAS-CONFIG-LANG', lang);
    changeLanguage(lang);
  };

  useEffect(() => {
    const token = PubSub.subscribe(TABS_STICKY_STATUS, (_: string, payload: boolean) => setIsSticky(payload));
    return () => {
      PubSub.unsubscribe(token);
    };
  }, []);

  const logo = language === ZH ? YAOTAI_LOGO_BLACK : YAOTAI_LOGO_BLACK_EN;
  return (
    <div className={cx('header')} style={style}>
      <div className={cx('content-wrapper')}>
        {/* 在全局模式下不具备 oem 控制逻辑 */}
        {oemPageMode === 'user' ? (
          <a href={isMainLogoOemCustom ? '#' : window.location.origin}>
            <img className={cx('logo')} src={logo} alt="logo" />
          </a>
        ) : oemPageMode === 'activity' && oem?.oem_log_lt_logo?.status === true ? (
          <a href={isMainLogoOemCustom ? '#' : window.location.origin}>
            <img className={cx('logo')} src={logo} alt="logo" />
          </a>
        ) : (
          <div className={cx('logo')}></div>
        )}
        <div className={cx('right-content')}>
          <Space size={28}>
            {!isSticky && (
              <>
                {isHasConfigLang && (
                  <p className={cx('i18n')} onClick={() => changeLang()}>
                    <span className={cx({ active: language === ZH })}>中</span>
                    &nbsp;/&nbsp;
                    <span className={cx({ active: language === EN })}>EN</span>
                  </p>
                )}
                {isLogin && (oem?.oem_manual?.status || !currentAid) && (
                  <a
                    href={!currentAid ? '/docs/zh/' : oem?.oem_manual?.detail || '/docs/zh/'}
                    rel="noreferrer"
                    target="_blank">
                    {t('homepage.userGuide')}
                  </a>
                )}
                {checkIsYaotaiUser(login_type) && (oem?.oem_cs?.status || !currentAid) && (
                  <a
                    onClick={() =>
                      ysfOpen({
                        userInfo: userInfo,
                        name: defaultCardInfo.name,
                        activity: activityName,
                      })
                    }>
                    {t('homepage.customerService')}
                  </a>
                )}
                {isLogin && hasSpacePermission && (
                  <a href={getCorporateUrl()} rel="noreferrer" target="_blank">
                    {t('homepage.corporateSpace')}
                  </a>
                )}
              </>
            )}
            {isSticky && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: '1',
                      label: (
                        <p className={cx('i18n')} onClick={() => changeLang()}>
                          <span style={{ color: language === ZH ? '#4e5969' : 'rgba(78, 89, 105, 0.5)' }}>中</span>
                          &nbsp;/&nbsp;
                          <span style={{ color: language === EN ? '#4e5969' : 'rgba(78, 89, 105, 0.5)' }}>EN</span>
                        </p>
                      ),
                      visible: isHasConfigLang,
                    },
                    {
                      key: '3',
                      label: (
                        <a href="/docs/zh/" rel="noreferrer" target="_blank">
                          {t('homepage.userGuide')}
                        </a>
                      ),
                      visible: isLogin && (oem?.oem_manual?.status || !currentAid),
                    },
                    {
                      key: '4',
                      label: (
                        <a
                          onClick={() =>
                            ysfOpen({
                              userInfo: userInfo,
                              name: defaultCardInfo.name,
                              activity: activityName,
                            })
                          }>
                          {t('homepage.customerService')}
                        </a>
                      ),
                      visible: checkIsYaotaiUser(login_type) && (oem?.oem_cs?.status || !currentAid),
                    },
                    {
                      key: '5',
                      label: (
                        <a href={getCorporateUrl()} rel="noreferrer" target="_blank">
                          {t('homepage.corporateSpace')}
                        </a>
                      ),
                      visible: isLogin && hasSpacePermission,
                    },
                  ].filter(item => item.visible !== false) as ItemType[],
                }}>
                <EeveeIcon style={{ cursor: 'pointer' }} name="gengduo" />
              </Dropdown>
            )}
            {isLogin ? (
              <PCHeaderDropAvatar
                isMainVersion={import.meta.env.VITE_MAIN === 'true'}
                isActivityHomePage={isActivityHomePage}
              />
            ) : (
              <a
                href={`/${SubPackageSlugEnum.Center}/${login}${
                  isActivityHomePage ? `?next=${encodeNextUrl(window.location.href)}` : ``
                }`}>
                {t('homepage.login')}
              </a>
            )}
          </Space>
        </div>
      </div>
    </div>
  );
};

export default NavHeader;
